import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, MessageCircle, Users as UsersIcon, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, telLink, waLink, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge, AccountBalanceBadge } from "@/components/common";
import PatientFormDialog from "@/components/PatientFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Patients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [service, setService] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, status, limit: 100 };
      if (service !== "all") params.service_type = service;
      const { data } = await api.get("/patients", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [search, status, service]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 300);
    return () => clearTimeout(timer.current);
  }, [load]);

  const isPhysio = user?.role === "physiotherapist";

  return (
    <div>
      <PageHeader title={isPhysio ? "My Patients" : "Patients"} subtitle={`${total} patient${total !== 1 ? "s" : ""}`} testid="patients-page">
        {!isPhysio && (
          <Button onClick={() => setOpen(true)} data-testid="add-patient-btn" className="bg-bk-green hover:bg-bk-greenDark">
            <Plus className="h-4 w-4 mr-1" /> Register Patient
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="patient-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone or Patient ID..." className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="discontinued">Discontinued</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All Patients</SelectItem>
          </SelectContent>
        </Select>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-service"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            <SelectItem value="clinic">Clinic</SelectItem>
            <SelectItem value="home">Home Visit</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState icon={UsersIcon} title="No patients found" subtitle="Try a different search or register a new patient." />
      ) : (
        <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => {
            const tStatus = p.treatment_status || (p.status === "archived" ? "archived" : "active");
            return (
              <Card key={p.id} data-testid={`patient-card-${p.patient_id}`} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/patients/${p.id}`)}>
                <div className="flex items-start gap-3">
                  <Avatar className="h-11 w-11">
                    <AvatarFallback className="bg-bk-lightGreen text-bk-green font-semibold">{initials(p.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                      <Badge variant="outline" className="text-[10px] shrink-0 border-slate-200 text-slate-500">{p.patient_id}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{p.problem}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <StatusBadge status={tStatus} />
                      <Badge variant="outline" className="capitalize text-[10px] bg-slate-50">{p.service_type}</Badge>
                      {p.next_visit_date && (
                        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                          Next: {p.next_visit_date}
                        </Badge>
                      )}
                      <AccountBalanceBadge balance={p.account_balance ?? p.advance_balance} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <a href={telLink(p.phone)} className="flex-1"><Button variant="outline" size="sm" className="w-full h-8 text-bk-blue" data-testid={`call-${p.patient_id}`}><Phone className="h-3.5 w-3.5" /></Button></a>
                  <a href={waLink(p.whatsapp || p.phone)} target="_blank" rel="noreferrer" className="flex-1"><Button variant="outline" size="sm" className="w-full h-8 text-bk-green" data-testid={`wa-${p.patient_id}`}><MessageCircle className="h-3.5 w-3.5" /></Button></a>
                  <Button variant="outline" size="sm" className="flex-1 h-8" onClick={() => navigate(`/patients/${p.id}`)}>Profile</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <PatientFormDialog open={open} onOpenChange={setOpen} onSaved={(p) => navigate(`/patients/${p.id}`)} />
    </div>
  );
}
